import classNames from "classnames";
import { Dialog, DialogPanel, DialogTitle, Transition } from "@headlessui/react";
import React, { CSSProperties, ComponentProps, Fragment } from "react";
// import { Box, Children, Space, Stack } from "@fastlooptech/layout-components";
import { spaceUnit } from "@/helpers/utils.helper";
import Button from "../Button";
import { useResponsive } from "@/client/hooks/useResponsive";
import { Body, Heading } from "@/client/foundations/Typography";
import { Box, Space } from "@/layouts/LayoutComponents";
import { Children, ImageType } from "@/types/app.type";
import ImageWrapper from "../ImageWrapper";
// import { Children } from "@fastlooptech/layout-components";


export enum ModalDirection {
  BottomUp = "bottomup",
  RightLeft = "rightleft",
  TopDown = "topdown",
}

export interface ModalProps {

  "data-test-id"?: string;
  /**
   * Modal id.
   * @type string;
   */
  id?: string;
  /**
   * Modal title.
   * @type string;
   */
  title?: string;
  /**
   * Modal sub title.
   * @type string;
   */
  subTitle?: string;
  /**
   * Modal description.
   * @type string;
   */
  description?: string;
  /**
   * Modal close handler.
   * @type () => void;
   * @required
   */
  onClose: () => void;
  /**
   * Panel back handler.
   * @type () => void;
   * @required
   */
  onBack?: () => void;
  /**
   * open the modal if true.
   * @type boolean;
   * @required
   */
  open: boolean;
  /**
   * renders modal without padding.
   * @type boolean;
   */
  full?: boolean;
  /**
   * Max width of the modal.
   * @type string
   */
  maxWidth?: string;
  /**
   * Optional className of the modal.
   * @type string
   */
  scrollable?: boolean;
  /**
   * Optional className of the modal.
   * @type string
   */
  className?: string;
  /**
   * Optional className of the modal content.
   * @type string
   */
  classNameContent?: string;
  /**
   * Children of the modal.
   * @type ComponentProps<typeof Box>["children"]
   * @required
   */
  children: ComponentProps<typeof Box>["children"];
  /**
   * Button to render in the footer.
   * @type {label: string; action?: () => void: className: string}
   * @optional
   */
  footerButton?: { label: string; action?: () => void; className?: string };
  /**
   * optional element of the modal footer.
   * @type ComponentProps<typeof Box>["children"]
   * @required
   */
  footerElement?: ComponentProps<typeof Box>["children"];
  /**
   * Hide a close button in the header
   * @type ReactNode
   * @optional
   */
  closeButton?: any;
  /**
   * Back button
   * @type boolean
   * @optional
   */
  backButton?: boolean;
  /**
   * Toggle Logo
   * @type boolean
   * @optional
   */
  showLogo?: boolean;

  /**
   * Header children
   * @type ComponentProps<typeof Box>["children"]
   * @optional
   */
  headerChildren?: ComponentProps<typeof Box>["children"];
  /**
   * Direction of the modal
   * @type ModalVariant | { enter: ModalVariant; leave: ModalVariant; };
   * @required
   */
  direction:
  | ModalDirection
  | {
    enter: ModalDirection;
    leave: ModalDirection;
  };
  /**
   * No animate the enter of the modal
   * @type boolean
   * @optional
   */
  noAnimateEnter?: boolean;
  /**
   * No animate the exit of the modal
   * @type boolean
   * @optional
   */
  noAnimateLeave?: boolean;
  /**
   * Header Right button
   * @type ComponentProps<typeof Button>["children"]
   */
  headerRightButton?: ComponentProps<typeof Button>["children"];
  /**
   * Hide the header
   * @type boolean
   * @optional
   * @default false
   * */
  customHeader?: Children;
  /**
   * Panel custom styles
   * @type string
   * @optional
   * */
  panelStyles?: CSSProperties;
  /**
   * Panel custom styles
   * @type string
   * @optional
   * */
  modalStyles?: CSSProperties;
  /**
   * Modal custom classNames
   * @type string
   * @optional
   * */
  panelClassNames?: string;
  /**
   * Modal Variant.
   * @type string;
   * @optional
   */
  modalVariant?: string;
  /**
   * Modal Variant.
   * @type string;
   * @optional
   */
  variant?: string;
  image?: ImageType;
}

const direction = {
  [ModalDirection.BottomUp]: {
    enterFrom: "top-full",
    enterTo: "top-0",
    leaveFrom: "top-0",
    leaveTo: "top-full",
  },
  [ModalDirection.RightLeft]: {
    enterFrom: "left-full",
    enterTo: "left-0",
    leaveFrom: "left-0",
    leaveTo: "left-full",
  },
  [ModalDirection.TopDown]: {
    enterFrom: "bottom-full",
    enterTo: "bottom-0",
    leaveFrom: "bottom-0",
    leaveTo: "bottom-full",
  },
};

export default function Modal({ showLogo = false, ...props }: ModalProps) {
  const { scrollable = true } = props;
  const { isXl, isMd, isSm } = useResponsive();
  // const modalBackgroundColor = () => {
  //   if(props?.modalVariant === "futures"){
  //     return `bg-modal-${props?.modalVariant}-bg`
  //   }else{
  //     return 'bg-modal-default-bg'
  //   }
  // } 
  const bgColors = {
    "bg-[#FFFFFF]": "#FFFFFF",
    "bg-[#ffdb00]": "#ffdb00",
    "bg-[#0f102a]": "#0f102a"
  }

  return (
    <Transition show={props.open} as={"div"}>
      <Dialog id={props.id}
        // onClose={props.onClose} 
        static
        onClose={() => null}
        className={classNames("bp-modal relative z-[110]")}>
        {/* Backdrop */}
        <Transition.Child
          as={"div"}
          enter={classNames("ease-out delay-100", {
            "duration-200": !props.noAnimateEnter,
            "duration-0": props.noAnimateEnter,
          })}
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={classNames("ease-out delay-100", {
            "duration-200": !props.noAnimateLeave,
            "duration-0": props.noAnimateLeave,
          })}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Box
            data-test-id={props["data-test-id"] + "-backdrop"}
            id="modal-backdrop"
            className="fixed inset-0 z-10 opacity-50 mix-blend-multiply bg-interactive-black bg-opacity-80"
            aria-hidden="true"
          />
        </Transition.Child>
        {/* Panel */}
        <Box
          data-test-id={props["data-test-id"]}
          column
          className={classNames("fixed inset-0 overflow-y-auto z-50", props.panelClassNames)}
          hAlignContent="center"
          vAlignContent="center"
          style={props.panelStyles}
        >
          <Transition.Child
            as={"div"}
            enter={classNames("ease-out", {
              "duration-200": !props.noAnimateEnter,
              "duration-0": props.noAnimateEnter,
            })}

            className={"w-full h-full flex flex-col justify-center items-center"}
            enterFrom={
              isXl
                ? "opacity-0 scale-95"
                : direction[typeof props.direction === "object" ? props.direction.enter : props.direction].enterFrom
            }
            enterTo={
              isXl
                ? "opacity-100 scale-100"
                : direction[typeof props.direction === "object" ? props.direction.enter : props.direction].enterTo
            }
            leave={classNames("ease-in", {
              "duration-200": !props.noAnimateLeave,
              "duration-0": props.noAnimateLeave,
            })}
            leaveFrom={
              isXl
                ? "opacity-100 scale-100"
                : direction[typeof props.direction === "object" ? props.direction.leave : props.direction].leaveFrom
            }
            leaveTo={
              isXl
                ? "opacity-0 scale-95"
                : direction[typeof props.direction === "object" ? props.direction.leave : props.direction].leaveTo
            }
          >
            <Box className={
              classNames("relative overflow-hidden inset-0 m-auto rounded-[0] md:rounded-modal",
                // `${props?.modalVariant === "futures" ? "!rounded-[0]" : ""}`,


                // UPDATE-----------------------------------------------
                // `${props?.modalVariant === "futures"? "h-full w-full flex justify-center items-center bg-modal-futures-bg" : "bg-modal-default-bg"}`
              )}
              style={{ maxWidth: props?.maxWidth }}>
              <DialogPanel
                as="div"
                data-test-id={props["data-test-id"] + "-panel"}
                className={classNames("relative inset-0 flex flex-col pb-4",
                  // UPDATE-----------------------------------------------
                  // `${props?.modalVariant === "futures" ? "bg-modal-futures-bg" : "bg-modal-default-bg"}`,
                  props.className, {
                  "w-screen h-screen": !isXl,
                  "w-fit h-fit": isXl,
                })}
                style={{ backgroundColor: bgColors[props?.variant as keyof typeof bgColors], ...props.modalStyles }}
              >
                {/* {!!props.closeButton && (props?.modalVariant !== "futures") && ( */}
                {!!props.closeButton && (
                  <Box className="w-full flex sticky justify-end px-3 pt-3 pb-1 z-[112]">
                    <Box
                      id="modal-close-button"
                      className="fixed"
                      data-test-id={(props?.["data-test-id"] ? `${props["data-test-id"]}-` : "") + "close-button"}
                      onClick={props?.onClose}
                      shrink={0}
                      hAlignContent="right"
                      style={{ fontSize: "25px" }}
                    >
                      {props.closeButton || <>&#x2715;</>}
                    </Box>
                  </Box>
                )}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                  {/* <Box column grow className="flex justify-between" style={{height: `${props?.modalVariant === "futures" ? "95vh" : "auto" }`, maxHeight: "95vh"}}> */}
                  <Box column grow className="flex justify-between" style={{ height: `${"auto"}`, maxHeight: "80vh" }}>
                    {/* Header */}
                    {props.customHeader ? (
                      props.customHeader
                    ) : (
                      <div className="px-4">
                        <DialogTitle>
                          <Box vAlignContent="center" hAlignContent="center" className="w-full sticky flex !flex-col">
                            <>
                              {showLogo && <ImageWrapper
                                src={props?.image?.src}
                                // width={mediaWidth || image?.width}
                                // height={mediaHeight || image?.height} // not important but required to omit  build errors
                                width={props?.image?.width}
                                height={props?.image?.height} // not important but required to omit  build errors
                                alt="card-image"
                                placeholder="blur"
                                blurDataURL={props?.image?.src}
                                // loading="lazy"
                                loader={({ src }) => src}
                                className="object-cover max-w-none mb-4"
                                style={{
                                  width: props?.image?.width || "163px",
                                  height: props?.image?.height || "44px",
                                  // left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
                                  // top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) :  `-${mediaStartViewY}`,
                                }}
                              // data-aos={props?.animated ? props?.animation ?? "fade-in" : ""}
                              />}
                              {!!props.title && (
                                <Heading component="h2" className="text-center w-full font-modal-heading pl-modal-heading-x mb-3" size="l" data-test-id={props["data-test-id"] + "-heading-title"}>
                                  {props.title}
                                </Heading>
                              )}
                              <Space fluid />
                            </>
                            {!!props.headerRightButton && (
                              <>
                                {props.headerRightButton}
                                <Space size={spaceUnit} />
                              </>
                            )}
                          </Box>
                        </DialogTitle>
                        {!!props.headerChildren && props.headerChildren}
                      </div>
                    )}
                    {/* Content */}
                    <Box grow column className={classNames("px-4 pb-4", { "overflow-y-auto": scrollable }, { "overflow-hidden": !scrollable })}>
                      <>
                        {!!props.subTitle && (
                          <Heading component="h5" className="text-center w-full font-modal-heading pl-modal-heading-x mb-1" size="m" data-test-id={props["data-test-id"] + "-heading-sub-title"}>
                            {props.subTitle}
                          </Heading>
                        )}
                        {!!props.description && (
                          <Body
                            size="s"
                            className="text-center mb-4"
                          // animated={props?.animated ?? false}
                          // animation={props?.animation ?? ""}
                          >
                            {props?.description}
                          </Body>
                        )}
                        {/* <p>{props.modalVariant|| "nothing"}</p> */}
                        {/* {props.open && props.children} */}
                        {props.children}
                      </>
                    </Box>
                    {/* Footer */}
                    {!!props.footerElement && props.footerElement}
                  </Box>
                  {/* {!!props.closeButton && (props?.modalVariant === "futures") && (
                    <Box className="w-full flex justify-center p-3">
                      <Box
                        id="modal-close-button"
                        data-test-id={props["data-test-id"] + "-close-button"}
                        onClick={props.onClose}
                        shrink={0}
                        hAlignContent="center"
                      >
                        {props.closeButton}
                      </Box>
                    </Box>
                  )} */}
                </div>
              </DialogPanel>
            </Box>
          </Transition.Child>
        </Box>
      </Dialog>
    </Transition>
  );
}
